<script setup lang="ts">
import { mdiCheck, mdiClose } from '@mdi/js'
import type { GrimoireItem } from '~/types/interfaces'

interface ProjectFileInfo {
  name: string
  alias?: string
  readonly: boolean
}

interface PickedPage {
  projectId: string
  name: string
}

interface Source {
  projectId: string
  fileNames: string[]
}

const props = defineProps<{
  items: GrimoireItem[]
}>()

const emit = defineEmits<{
  submit: [sources: Source[]]
}>()

const dialogStore = useDialogStore()
const dialog = dialogStore.remixSource
const dialogParams = computed(() => dialog.params)

const api = useApi()
const { $sound } = useNuxtApp()

// `$sound.playSound(...)` は同期 (void) と非同期 (Promise<void>) の両方の戻り値を持ちうる。
// SE の失敗は UX に影響させたくないので fire-and-forget で握りつぶす。
function playSe(name: string): void {
  try {
    const result = $sound.playSound(name)
    if (result instanceof Promise) result.catch(() => {})
  }
  catch {
    /* ignore */
  }
}

// --- State ---

// 一冊まるごと選ぶ (false, 既定) / ページ単位で選ぶ (true) のモード切替。
// 既定はシンプルな「冊まるごと」。アドバンスドな使い方をしたい人だけ ON にする。
const pagewiseMode = ref(false)

const openedGrimoireId = ref<string | null>(null)
const filesByProject = ref<Record<string, ProjectFileInfo[]>>({})
const fetchingProjects = ref<Set<string>>(new Set())
const pickedPages = ref<PickedPage[]>([])

watch(
  () => dialog.visible,
  (visible) => {
    if (visible) {
      openedGrimoireId.value = null
      filesByProject.value = {}
      pickedPages.value = []
      fetchingProjects.value = new Set()
      // モードはダイアログを跨いで保持しない (ユーザーが意識せず混乱しないよう既定 OFF)
      pagewiseMode.value = false
    }
  },
)

// --- Derived ---

const remixableItems = computed(() =>
  props.items.filter(item => ['user', 'encodia', 'privilege', 'crystal'].includes(item.type)),
)

const groupedItems = computed(() => {
  const order: ReadonlyArray<{ type: string, label: string }> = [
    { type: 'user', label: 'あなたの魔導書' },
    { type: 'encodia', label: 'エンコーディアの魔導書' },
    { type: 'privilege', label: '研究会特典' },
    { type: 'crystal', label: 'デコード済み魔導書' },
  ]
  return order
    .map(({ type, label }) => ({
      type,
      label,
      items: remixableItems.value.filter(item => item.type === type),
    }))
    .filter(group => group.items.length > 0)
})

const openedGrimoire = computed(() =>
  remixableItems.value.find(item => item.id === openedGrimoireId.value) ?? null,
)

// バックエンド `next_unique_name` (lib/src/gennai/services/clone_project.py) と同等。
// 連続ドット拡張子 (foo.reference.py) も保存する。
function nextUniqueName(existing: ReadonlySet<string>, desired: string): string {
  if (!existing.has(desired)) return desired
  const m = desired.match(/^([^.]+)((?:\.[^.]+)+)$/)
  let stem: string
  let ext: string
  if (m) {
    stem = m[1] ?? desired
    ext = m[2] ?? ''
  }
  else {
    stem = desired
    ext = ''
  }
  let n = 2
  while (true) {
    const candidate = `${stem}_${n}${ext}`
    if (!existing.has(candidate)) return candidate
    n++
  }
}

const remappedPickedPages = computed(() => {
  const used = new Set<string>()
  return pickedPages.value.map((picked) => {
    const finalName = nextUniqueName(used, picked.name)
    used.add(finalName)
    return { ...picked, finalName }
  })
})

// 採用ページを 1 件以上持っている本の projectId 集合。
// 冊まるごとモードでは「この本が選ばれている」インジケータに使う。
// ページモードでも「この本にチェック済みのページがある」表示に使える。
const bookHasPicks = computed(() => {
  const set = new Set<string>()
  for (const p of pickedPages.value) set.add(p.projectId)
  return set
})

const emptyHint = computed(() =>
  pagewiseMode.value
    ? '本棚から使うページを選ぶ'
    : '本棚から使う本を選ぶ',
)

// ページ単位モードを OFF にしたらドロワーを閉じる (冊まるごとモードでは使わないため)
watch(pagewiseMode, (on) => {
  if (!on) openedGrimoireId.value = null
})

// --- Helpers ---

function getGrimoireColor(type: string): string {
  switch (type) {
    case 'user': return 'red'
    case 'encodia': return 'green'
    case 'privilege': return 'brown'
    case 'crystal': return 'blue'
    default: return 'red'
  }
}

// 拡張子別の絵文字。
//   .py     → 📜 巻物 (コードを記した魔法の巻物)
//   .pyxres → 👾 レトロピクセルモンスター (Pyxel = 8/16-bit ゲーム素材)
//   .pyxpal → 🎨 パレット (色の素)
function emojiFor(name: string): string {
  if (name.endsWith('.pyxres')) return '👾'
  if (name.endsWith('.pyxpal')) return '🎨'
  if (name.endsWith('.ogg')) return '🎵'
  if (name.endsWith('.png')) return '🖼️'
  return '📜'
}

function pageNumberOf(projectId: string, name: string): number {
  const files = filesByProject.value[projectId] ?? []
  const idx = files.findIndex(f => f.name === name)
  return idx >= 0 ? idx + 1 : 0
}

function sourceTitleOf(projectId: string): string {
  // 別タブで本が削除された場合などに備え、見つからない時は明示する
  return remixableItems.value.find(item => item.id === projectId)?.name ?? '(削除された本)'
}

// --- Actions ---

async function ensureFilesLoaded(projectId: string): Promise<void> {
  if (filesByProject.value[projectId]) return
  if (fetchingProjects.value.has(projectId)) return
  fetchingProjects.value.add(projectId)
  try {
    const project = await api.getProject(projectId)
    filesByProject.value[projectId] = project.files
  }
  catch (e) {
    console.error('[MultiRemixDialog] failed to load files for', projectId, e)
    filesByProject.value[projectId] = []
  }
  finally {
    fetchingProjects.value.delete(projectId)
  }
}

const onSelectGrimoire = async (id: string): Promise<void> => {
  if (pagewiseMode.value) {
    // ページ単位モード: ドロワーをトグル開閉。ページ採用は別操作。
    if (openedGrimoireId.value === id) {
      openedGrimoireId.value = null
      return
    }
    openedGrimoireId.value = id
    playSe('click')
    await ensureFilesLoaded(id)
    return
  }

  // 冊まるごとモード: 1 冊だけ選んで全ページを自動採用 (既存の選択は破棄)。
  // 同じ本を再タップしたら全部解除。違う本なら置き換え。
  await ensureFilesLoaded(id)
  const files = filesByProject.value[id] ?? []
  const isOnlyPickedBook
    = pickedPages.value.length > 0
      && pickedPages.value.length === files.length
      && pickedPages.value.every(p => p.projectId === id)
  if (isOnlyPickedBook) {
    pickedPages.value = []
    playSe('cancel')
  }
  else {
    pickedPages.value = files.map(f => ({ projectId: id, name: f.name }))
    playSe('click')
  }
  // 冊まるごとモードではドロワーを開かない
  openedGrimoireId.value = null
}

const onCloseDrawer = (): void => {
  openedGrimoireId.value = null
  playSe('cancel')
}

function isPicked(projectId: string, name: string): boolean {
  return pickedPages.value.some(p => p.projectId === projectId && p.name === name)
}

const onTogglePage = (projectId: string, name: string): void => {
  if (isPicked(projectId, name)) {
    pickedPages.value = pickedPages.value.filter(
      p => !(p.projectId === projectId && p.name === name),
    )
    playSe('cancel')
  }
  else {
    pickedPages.value = [...pickedPages.value, { projectId, name }]
    playSe('click')
  }
}

function pickedPageKey(picked: PickedPage): string {
  return `${picked.projectId}::${picked.name}`
}

const onSubmit = (): void => {
  if (pickedPages.value.length === 0) return

  const grouped = new Map<string, string[]>()
  for (const picked of pickedPages.value) {
    const list = grouped.get(picked.projectId) ?? []
    list.push(picked.name)
    grouped.set(picked.projectId, list)
  }
  const sources: Source[] = [...grouped.entries()].map(([projectId, fileNames]) => ({
    projectId,
    fileNames,
  }))
  emit('submit', sources)
}

const onCancel = (): void => {
  dialogStore.closeDialog('remixSource', 'cancel')
}
</script>

<template>
  <DialogLargeDialog
    v-if="dialogParams && dialog.visible"
    :title="dialogParams.title || '魔導書を編む'"
    :model-value="dialog.visible"
    :submit-button="{
      title: pickedPages.length > 0 ? `魔導書を編む (${pickedPages.length})` : '魔導書を編む',
      disabled: pickedPages.length === 0,
    }"
    :cancel-button="{ title: dialogParams.actions?.cancel?.label || 'やめる' }"
    @submit="onSubmit"
    @cancel="onCancel"
  >
    <template #hint>
      {{ pagewiseMode
        ? '新しい魔導書の元になる魔導書のページを選んでください。'
        : '新しい魔導書の元になる魔導書を選んでください。' }}
    </template>
    <template #default>
      <v-row
        no-gutters
        class="layout"
      >
        <!-- =========== 本棚 (Library) =========== -->
        <!-- 冊まるごとモードでは右ペイン (編み机) を隠して 1 冊選択 UX に集中させる。
             ページ単位モード ON のときだけ編み机が現れ、md=7/5 の 2 ペイン構成になる。 -->
        <v-col
          cols="12"
          :md="pagewiseMode ? 7 : 12"
          class="pane library"
          :class="{
            'has-drawer-open': openedGrimoireId !== null,
            'is-only-pane': !pagewiseMode,
          }"
        >
          <div class="pane-header">
            <h3 class="pane-title">
              本棚<span class="pane-title-sub">（持っている魔導書）</span>
            </h3>
            <v-checkbox
              v-model="pagewiseMode"
              label="複数の本を組み合わせる"
              density="compact"
              hide-details
              color="primary"
              class="mode-toggle"
              data-test-id="pagewise-mode-toggle"
            />
          </div>

          <div class="library-grimoires">
            <div
              v-for="group in groupedItems"
              :key="group.type"
              class="library-group"
            >
              <div class="group-label">
                {{ group.label }}
              </div>
              <div class="grimoire-grid">
                <div
                  v-for="item in group.items"
                  :key="item.id"
                  class="grimoire-cell"
                  :class="{
                    'is-opened': openedGrimoireId === item.id,
                    'is-picked': bookHasPicks.has(item.id),
                  }"
                  data-test-id="library-grimoire-slot"
                  @click="onSelectGrimoire(item.id)"
                >
                  <CradleGrimoireSlot
                    :scene-id="item.id"
                    :name="item.name"
                    :type="item.type"
                    :color="getGrimoireColor(item.type)"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- 下からせりあがるページ一覧 (drawer) -->
          <div
            class="library-drawer"
            :class="{ 'is-open': openedGrimoireId !== null }"
          >
            <div class="drawer-header">
              <span class="drawer-title">
                <template v-if="openedGrimoire">
                  開いた本: <strong>「{{ openedGrimoire.name }}」</strong>
                </template>
              </span>
              <v-btn
                :icon="mdiClose"
                size="small"
                variant="text"
                density="comfortable"
                aria-label="本を閉じる"
                data-test-id="library-drawer-close"
                @click="onCloseDrawer"
              />
            </div>
            <div class="drawer-body">
              <div
                v-if="openedGrimoireId && fetchingProjects.has(openedGrimoireId)"
                class="loading"
              >
                読み込み中…
              </div>
              <v-list
                v-else-if="openedGrimoireId"
                density="compact"
                class="pages-list"
              >
                <v-list-item
                  v-for="(file, i) in filesByProject[openedGrimoireId] ?? []"
                  :key="file.name"
                  :class="{ 'is-picked': isPicked(openedGrimoireId, file.name) }"
                  data-test-id="library-page-item"
                  @click="onTogglePage(openedGrimoireId!, file.name)"
                >
                  <template #prepend>
                    <span class="page-emoji">{{ emojiFor(file.name) }}</span>
                  </template>
                  <v-list-item-title>
                    <span class="page-number">{{ i + 1 }}ページ目</span>
                    <span class="filename">{{ file.name }}</span>
                    <span
                      v-if="file.alias"
                      class="alias"
                    >({{ file.alias }})</span>
                  </v-list-item-title>
                  <template #append>
                    <v-icon
                      v-if="isPicked(openedGrimoireId, file.name)"
                      :icon="mdiCheck"
                      color="success"
                    />
                  </template>
                </v-list-item>
              </v-list>
            </div>
          </div>
        </v-col>

        <!-- =========== 作業机 (Workbench) =========== -->
        <!-- 冊まるごとモードでは編み机を完全に非表示にしてシンプルな 1 ペイン UX に。 -->
        <v-col
          v-if="pagewiseMode"
          cols="12"
          md="5"
          class="pane workbench"
        >
          <div class="pane-header">
            <h3 class="pane-title">
              編み机<span class="pane-title-sub">（作っている魔導書）</span>
            </h3>
          </div>

          <div class="workbench-cover">
            <CradleGrimoireSlot
              scene-id="__workbench__"
              name="新しい魔導書"
              type="user"
              color="red"
            />
          </div>

          <div
            v-if="pickedPages.length === 0"
            class="empty-hint"
          >
            {{ emptyHint }}
          </div>
          <v-list
            v-else
            density="compact"
            class="pages-list workbench-pages-list"
          >
            <v-list-item
              v-for="picked in remappedPickedPages"
              :key="pickedPageKey(picked)"
              data-test-id="workbench-page-item"
              @click="onTogglePage(picked.projectId, picked.name)"
            >
              <template #prepend>
                <span class="page-emoji">{{ emojiFor(picked.name) }}</span>
              </template>
              <v-list-item-title class="source-title">
                「{{ sourceTitleOf(picked.projectId) }}」
              </v-list-item-title>
              <v-list-item-subtitle class="page-subtitle">
                <span class="page-number">{{ pageNumberOf(picked.projectId, picked.name) }}ページ目</span>
                <span class="filename">{{ picked.finalName }}</span>
                <span
                  v-if="picked.finalName !== picked.name"
                  class="rename-note"
                >
                  (元:「{{ picked.name }}」)
                </span>
              </v-list-item-subtitle>
              <template #append>
                <v-icon
                  :icon="mdiCheck"
                  color="success"
                />
              </template>
            </v-list-item>
          </v-list>
        </v-col>
      </v-row>
    </template>
  </DialogLargeDialog>
</template>

<style scoped>
/* ============== 全体レイアウト ============== */
/* centering43 レイアウト内なので、サイズは原則 calc(N * var(--u)) で指定する。
   var(--u) = min(1vw, 1.333vh) → 4:3 コンテナ幅の 1%。
   高解像度モニタで表示が破綻しないよう、rem/px 固定は最小値フォールバックのみで使う。 */

.layout {
  height: 100%;
  min-height: max(280px, calc(45 * var(--u)));
}

/* 各ペインのタイトル行。本棚側はモード切替チェックボックスを併置する。
   編み机側はタイトルだけだが、レイアウトの一貫性のため同じ wrapper を使う。 */
.pane-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: max(4px, calc(0.5 * var(--u)));
  flex-shrink: 0;
}

.pane-header .pane-title {
  margin: 0;
}

.mode-toggle {
  flex-shrink: 0;
  margin: 0;
  padding: 0;
}

.mode-toggle :deep(.v-label) {
  font-size: max(11.85px, calc(1.667 * var(--u)));
  color: #5c2c0c;
  opacity: 1;
}

.pane {
  height: 100%;
  padding: max(4px, calc(0.5 * var(--u))) max(6px, calc(0.75 * var(--u)));
  display: flex;
  flex-direction: column;
  min-height: 0;
}

.pane.library {
  border-right: 1px solid rgba(64, 32, 0, 0.15);
  position: relative;
  /* drawer がせり上がる際に内部要素をはみ出させない */
  overflow: hidden;
  /* 左端を「とじる」ボタンの左端に揃える: actions の padding が 0.625*--u */
  padding-left: max(5px, calc(0.625 * var(--u)));
}

/* 冊まるごとモード時は単独ペインなので、右端の境界線と padding を編み机
   ペインの右端基準に揃える (とじる/魔導書を編む ボタンと同じ 0.625*--u)。 */
.pane.library.is-only-pane {
  border-right: none;
  padding-right: max(5px, calc(0.625 * var(--u)));
}

/* 編み机:
   - 左: 中央の境界線に張り付かないようゆとりを持たせる
   - 右: 「魔導書を編む」ボタンの右端に揃える (= sheet 内縁から 0.625*--u) */
.pane.workbench {
  padding-left: max(12px, calc(1.5 * var(--u)));
  padding-right: max(5px, calc(0.625 * var(--u)));
}

.pane-title {
  margin: 0 0 max(2px, calc(0.25 * var(--u))) 0;
  font-size: max(13.33px, calc(1.875 * var(--u)));
  font-weight: bold;
  color: #5c2c0c;
  flex-shrink: 0;
  line-height: 1.4;
}

/* 「本棚」「編み机」の後ろに添える機能名を控えめに */
.pane-title-sub {
  font-size: 0.7em;
  font-weight: normal;
  color: #8b6432;
  margin-left: max(2px, calc(0.25 * var(--u)));
}

/* ============== 本棚 (上半分 — drawer 開いた時は縮む) ============== */

.library-grimoires {
  flex: 1 1 auto;
  /* 縦は内容に応じてスクロール、横は ✓ バッジ等の絶対配置で余分な
     スクロールバーが出ないように明示的に hidden にする */
  overflow-x: hidden;
  overflow-y: auto;
  min-height: 0;
  /* drawer が開閉する時にスムーズに縮む */
  transition: flex-basis 0.25s ease-out;
}

.library-group + .library-group {
  margin-top: max(4px, calc(0.5 * var(--u)));
}

.group-label {
  font-size: max(10.67px, calc(1.5 * var(--u)));
  color: #6b4520;
  margin-bottom: max(2px, calc(0.25 * var(--u)));
  line-height: 1.4;
}

.grimoire-grid {
  display: grid;
  /* 本のサイズはペイン幅に依らず一定にする (#615 brushup)。
     md=7 (編み机 ON) では 4 列、md=12 (編み机 OFF) では本のサイズはそのまま
     列数だけ増えてエリアにフローしていく。
     白い余白 (.grimoire の padding) は固定スケールなので、セル幅を大きく
     すると本のイラストの方が比率で大きく成長する。 */
  grid-template-columns: repeat(auto-fill, max(79px, calc(10.5 * var(--u))));
  gap: max(6px, calc(0.8 * var(--u)));
  /* グリッドの右端で ✓ バッジが本の外にはみ出すぶんを内側に確保しておく */
  padding-right: max(4px, calc(0.6 * var(--u)));
  /* 編み机 OFF の広いペインで右側にぽっかり余白が出ないよう中央寄せ */
  justify-content: center;
}

.grimoire-cell {
  cursor: pointer;
  transition: transform 0.1s ease-out;
  position: relative;
}

.grimoire-cell:hover {
  transform: translateY(-2px);
}

.grimoire-cell.is-opened {
  outline: max(2px, calc(0.3 * var(--u))) solid #c46060;
  outline-offset: max(1px, calc(0.2 * var(--u)));
  border-radius: max(3px, calc(0.4 * var(--u)));
}

/* 採用ページを 1 つ以上含む本に ✓ バッジを表示。
   冊まるごとモードでは「この本が選ばれている」証、
   ページモードでは「この本にチェック済みのページがある」証となる。
   サイズ・位置・影は --u スケールで本のサイズと釣り合いを保つ。 */
.grimoire-cell.is-picked::after {
  content: '✓';
  position: absolute;
  top: calc(-0.4 * var(--u));
  right: calc(-0.4 * var(--u));
  width: max(18px, calc(2 * var(--u)));
  height: max(18px, calc(2 * var(--u)));
  font-size: max(12px, calc(1.4 * var(--u)));
  line-height: 1;
  background: #4caf50;
  color: white;
  font-weight: bold;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: max(1px, calc(0.12 * var(--u))) max(1px, calc(0.12 * var(--u))) max(3px, calc(0.35 * var(--u)))
    rgba(0, 0, 0, 0.35);
  z-index: 5;
  pointer-events: none;
}

/* GrimoireSlot は cradle メイン画面用にデザインされていて、ダイアログ内では
   余白とタイトルが相対的に大きすぎる。縮小オーバーライド (#602 UX iter)。
   左ペインの本棚 (`.grimoire-grid`) と右ペインの制作中本 (`.workbench-cover`)
   の両方に同じスケーリングを適用する。 */
.grimoire-grid :deep(.grimoire),
.workbench-cover :deep(.grimoire) {
  padding: max(4px, calc(0.6 * var(--u))) max(2px, calc(0.4 * var(--u)));
  box-shadow: max(2px, calc(0.4 * var(--u))) max(2px, calc(0.4 * var(--u)))
    max(3px, calc(0.5 * var(--u))) rgba(0, 0, 0, 0.35);
}

.grimoire-grid :deep(.overlay-text),
.workbench-cover :deep(.overlay-text) {
  /* タイトルが long のときに見える字数を稼ぐため、フォントを縮め行数を 3 行に。
     GrimoireSlot 既定は -webkit-line-clamp: 2 なのでここで 3 に上書き。 */
  font-size: max(10.67px, calc(1.2 * var(--u)));
  line-height: 1.25;
  width: 95%;
  -webkit-line-clamp: 3;
}

.grimoire-grid :deep(.overlay-text p),
.workbench-cover :deep(.overlay-text p) {
  margin: 0;
}

/* ============== ページ一覧 drawer (下からせり上がる) ============== */

.library-drawer {
  /* 閉時は高さ 0 で消えている */
  flex: 0 0 0%;
  height: 0;
  min-height: 0;
  overflow: hidden;
  transition:
    flex-basis 0.25s ease-out,
    height 0.25s ease-out;
  background-color: rgba(255, 245, 220, 0.95);
  border-top: max(2px, calc(0.35 * var(--u))) solid #c46060;
  border-radius: max(4px, calc(0.6 * var(--u))) max(4px, calc(0.6 * var(--u))) 0 0;
  display: flex;
  flex-direction: column;
  margin: 0 calc(-0.25 * var(--u));
  /* 下からせり上がる感を出すため、shadow で浮かす */
  box-shadow: 0 max(-4px, calc(-0.5 * var(--u))) max(8px, calc(1.2 * var(--u))) rgba(0, 0, 0, 0.1);
}

.library-drawer.is-open {
  /* 開時は本棚の半分強を占める */
  flex: 0 0 60%;
  height: 60%;
}

.drawer-header {
  flex-shrink: 0;
  padding: max(3px, calc(0.4 * var(--u))) max(4px, calc(0.5 * var(--u))) max(2px, calc(0.25 * var(--u)));
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(64, 32, 0, 0.15);
}

.drawer-title {
  font-size: max(12.5px, calc(1.75 * var(--u)));
  color: #5c2c0c;
  line-height: 1.4;
}

.drawer-title strong {
  color: #c46060;
}

.drawer-body {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
  padding: max(2px, calc(0.25 * var(--u))) max(2px, calc(0.25 * var(--u)))
    max(4px, calc(0.5 * var(--u)));
}

/* ============== 作業机 ============== */

.workbench-cover {
  /* 左ペインの 1 マス (4 列グリッド + gap) と同じ視覚サイズにする。
     gap/padding を引かずに 25% にすると 1 マスより少し大きく見えるので少し縮める */
  width: 20%;
  min-width: max(72px, calc(11 * var(--u)));
  margin: 0 auto max(4px, calc(0.5 * var(--u)));
  flex-shrink: 0;
}

.empty-hint {
  margin-top: max(4px, calc(0.5 * var(--u)));
  padding: max(6px, calc(0.75 * var(--u)));
  background-color: rgba(255, 255, 255, 0.4);
  border-radius: max(3px, calc(0.4 * var(--u)));
  font-size: max(12.5px, calc(1.75 * var(--u)));
  color: #6b4520;
  text-align: center;
  line-height: 1.4;
}

.workbench .pages-list {
  flex: 1 1 auto;
  overflow-y: auto;
  min-height: 0;
}

/* ============== ページ行 (本棚側 / 机側 共通) ============== */

.pages-list {
  background-color: transparent;
  padding: 0;
}

.pages-list :deep(.v-list-item) {
  cursor: pointer;
  border-radius: max(3px, calc(0.4 * var(--u)));
  margin-bottom: max(1px, calc(0.15 * var(--u)));
  /* Vuetify 既定の min-height / --v-list-item-min-height を解除し、中身の
     高さに合わせて item が伸びるようにする。padding で視覚的な余白を確保。 */
  min-height: 0;
  --v-list-item-min-height: 0;
  padding-block: max(4px, calc(0.5 * var(--u)));
  padding-inline-start: max(6px, calc(0.75 * var(--u)));
  padding-inline-end: max(6px, calc(0.75 * var(--u)));
}

/* Vuetify は `.v-list-item__content` に `overflow: hidden` をかけており、
   item の min-height が content より小さい場合に文字下部 (descender) を
   clip してしまう。content 自身に min-height を強制させない + overflow を
   visible にして、ディセンダーが切れないようにする。 */
.pages-list :deep(.v-list-item__content) {
  min-height: 0;
  overflow: visible;
}

.pages-list :deep(.v-list-item:hover) {
  background-color: rgba(255, 255, 255, 0.5);
}

/* prepend (絵文字) と append (✓) を 2 行コンテンツの中央に揃える */
.pages-list :deep(.v-list-item__prepend),
.pages-list :deep(.v-list-item__append) {
  align-self: center;
}

.pages-list :deep(.is-picked .v-list-item-title) {
  color: #6b4520;
  opacity: 0.6;
}

.pages-list :deep(.v-list-item-title) {
  font-size: max(13.33px, calc(1.875 * var(--u)));
  display: flex;
  align-items: center;
  gap: max(3px, calc(0.4 * var(--u)));
  flex-wrap: wrap;
  /* Vuetify 既定の line-clamp / nowrap / line-height: 1.5rem を解除。
     line-height: 1.5rem は固定で、フォントが --u でスケールするとディセンダーが
     見切れる。1.4 (unitless) でフォントに連動させる。 */
  white-space: normal;
  -webkit-line-clamp: unset;
  text-overflow: clip;
  overflow: visible;
  line-height: 1.4;
}

/* 絵文字ベースのページアイコン: 大きめ + ふんわり影でセクシーに */
.page-emoji {
  font-size: max(20px, calc(2.8 * var(--u)));
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: max(25px, calc(3.5 * var(--u)));
  height: max(25px, calc(3.5 * var(--u)));
  filter: drop-shadow(1px 1px 1px rgba(0, 0, 0, 0.25));
  flex-shrink: 0;
  /* 採用済みでも色は変えず、薄くするのは v-list-item-title 側で行う */
}

.pages-list :deep(.is-picked) .page-emoji {
  opacity: 0.5;
}

/* 机側のページ行: ソース魔導書名 (タイトル) + ページ情報 (サブタイトル) の 2 行 */
.workbench-pages-list :deep(.v-list-item-title.source-title) {
  display: block;
  font-size: max(12.5px, calc(1.75 * var(--u)));
  font-weight: bold;
  color: #5c2c0c;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  /* line-height: 1.5rem (Vuetify 既定) はフォントが --u でスケールすると不足 */
  line-height: 1.4;
}

.workbench-pages-list :deep(.v-list-item-subtitle.page-subtitle) {
  display: flex;
  align-items: center;
  gap: max(3px, calc(0.4 * var(--u)));
  flex-wrap: wrap;
  opacity: 0.85;
  font-size: max(11.85px, calc(1.667 * var(--u)));
  margin-top: max(1px, calc(0.15 * var(--u)));
  /* Vuetify 既定の line-clamp / nowrap / line-height: 1rem を全て解除 */
  white-space: normal;
  -webkit-line-clamp: unset;
  text-overflow: clip;
  overflow: visible;
  line-height: 1.4;
}

.page-number {
  font-weight: bold;
  color: #5c2c0c;
  white-space: nowrap;
}

.filename {
  font-family: monospace;
  /* em は親 (subtitle / title) のフォントサイズに連動 */
  font-size: 0.9em;
  color: #404040;
  word-break: break-all;
}

.alias {
  font-size: 0.9em;
  color: #6b4520;
}

.rename-note {
  font-size: 0.85em;
  color: #c46060;
}

.loading {
  padding: max(4px, calc(0.5 * var(--u)));
  font-size: max(12.5px, calc(1.75 * var(--u)));
  color: #6b4520;
}

/* ============== モバイル: 縦積み ============== */

@media (max-width: 959px) {
  .layout {
    /* スマホでは固定高さを解除して内容に応じて伸ばす (ダイアログ自体がスクロール) */
    height: auto;
  }
  .pane {
    height: auto;
    min-height: 200px;
  }
  .pane.library {
    border-right: none;
    border-bottom: 1px solid rgba(64, 32, 0, 0.15);
  }
}
</style>
